import Notes from '@components/notes/notes';
import useEnvService from '@hooks/app/useEnvService';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import AttachmentsGallery from '../../components/attachments-gallery';
import ChatTabs from '../../components/chat-tabs';
import { ChatDirectWindow } from '../../components/chat-window';
import SearchInput from '../../components/search-input/search-input';
import { actions as chatActions } from '../../store/ducks/direct-chat';
import { actions as lifeIdActions } from '../../store/ducks/life-id';
import { actions } from '../../store/ducks/patients';
import { actions as userActions } from '../../store/ducks/users';
import TStore from '../../types/store';
import { TSearchParams } from '../../types/users';
import BaseInfo from './components/base-info';
import Programs from './components/programs/programs';
import TweelioVideoDraggable from './components/tweelio-video-draggable';
import VitalSidebar from './components/vital-sidebar';
import { LifeIdSC, MainColSC, NoConnectionSC, VitalColSC } from './life-id.styled';

const INTERVAL = 1000 * 30;

type TProps = {} & RouteComponentProps &
  ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const LifeId: React.FC<TProps> = ({
  match,
  connectionStatus,
  messagesLoading,
  providerId,
  patientDetail,
  twilioRoomData,
  twilioErrorMessage,
  getMessagesRequest,
  setReceiver,
  resetMessages,
  postSearchRequest,
  getPatientDetailRequest,
  getGeneralInfoRequest,
  getVitalDataById,
  getBehavioralDataById,
  getVitalDataIntervals,
}) => {
  const { userId, tab }: any = match.params;
  const jabberId = patientDetail?.jabberId;
  const [activeTab, setActiveTab] = useState(tab || 'CHAT');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [env] = useEnvService();

  const chatWithHilbi = userId === env.xmpp.hilbiId;

  const onChatSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  useEffect(() => {
    getGeneralInfoRequest(providerId, userId);
    const interval = setInterval(() => {
      getGeneralInfoRequest(providerId, userId);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [providerId, userId, getGeneralInfoRequest]);

  useEffect(() => {
    getPatientDetailRequest(providerId, userId);
    const interval = setInterval(() => {
      getPatientDetailRequest(providerId, userId);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [providerId, userId, getPatientDetailRequest]);

  useEffect(() => {
    getVitalDataById(providerId, userId);
    const interval = setInterval(() => {
      getVitalDataById(providerId, userId);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [providerId, userId, getVitalDataById]);

  useEffect(() => {
    getBehavioralDataById(userId);
    const interval = setInterval(() => {
      getBehavioralDataById(userId);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [userId, getBehavioralDataById]);

  useEffect(() => {
    getVitalDataIntervals(providerId, userId);
    const interval = setInterval(() => {
      getVitalDataIntervals(providerId, userId);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [providerId, userId, getVitalDataIntervals]);

  useEffect(() => {
    if (connectionStatus === 'CONNECTED' && jabberId) {
      getMessagesRequest(jabberId);
      setReceiver(jabberId);
      postSearchRequest(providerId, { jabberIds: [jabberId] });
    }
  }, [connectionStatus, providerId, jabberId]);

  useEffect(() => {
    return () => {
      resetMessages();
    };
  }, [resetMessages]);

  useEffect(() => {
    if (!!twilioRoomData) {
      setActiveTab('VIDEO');
    } else {
      setActiveTab('CHAT');
    }
  }, [twilioRoomData]);

  return (
    <>
      {/* <LifeIdBreadcrumbs /> */}
      <LifeIdSC>
        <MainColSC>
          <BaseInfo videoIndicatorStatus={!!twilioRoomData} />
          <div className="life-id-chat">
            <TweelioVideoDraggable activeTab={activeTab} />
            <div className="life-id-chat__first-line">
              <SearchInput isBlock onSearch={onChatSearch} />
              <ChatTabs
                selected={activeTab}
                onChange={setActiveTab}
                isVideo={!!twilioRoomData && !twilioErrorMessage}
              />
            </div>
            {activeTab === 'CHAT' ? (
              connectionStatus === 'FAILED' ? (
                <NoConnectionSC>
                  <p>
                    <FormattedMessage id="chat.no_connection_title" />
                  </p>
                </NoConnectionSC>
              ) : (
                <ChatDirectWindow searchQuery={searchQuery} />
              )
            ) : activeTab === 'NOTES' ? (
              <Notes userId={userId} />
            ) : activeTab === 'TOOLS' ? (
              <Programs userId={userId} />
            ) : connectionStatus === 'FAILED' ? (
              <NoConnectionSC>
                <p>
                  <FormattedMessage id="chat.no_connection_title" />
                </p>
              </NoConnectionSC>
            ) : (
              <AttachmentsGallery type="DIRECT_CHAT" messagesLoading={messagesLoading} />
            )}
          </div>
        </MainColSC>
        {!chatWithHilbi && (
          <VitalColSC>
            <VitalSidebar userId={userId} />
          </VitalColSC>
        )}
      </LifeIdSC>
    </>
  );
};

const mapStateToProps = (store: TStore) => ({
  connectionStatus: store.xmpp.status,
  messagesLoading: store.directChat.isLoading,
  patientDetail: store.patients.patientDetail,
  twilioRoomData: store.twilio.twilioRoomData,
  // eVisitCurrentEvent: store.eVisits.singleEvisitItem,
  twilioErrorMessage: store.twilio.twilioErrorMessage,
  providerId: store.users.business[0].id,
  reqStatus: store.twilio.joinTwilioRoomRequestStatus,
});

const mapDispatchToProps = (dispatch: any) => ({
  getPatientDetailRequest: (providerId: string, id: string) =>
    dispatch(actions.getPatientDetailRequest(providerId, id)),
  getMessagesRequest: (from: string) => dispatch(chatActions.getMessagesRequest(from)),
  setReceiver: (receiver: string) => dispatch(chatActions.setReceiver(receiver)),
  postSearchRequest: (providerId: string, query: TSearchParams) =>
    dispatch(userActions.postSearchRequest({ providerId, ...query })),
  resetMessages: () => dispatch(chatActions.resetMessages()),
  getGeneralInfoRequest: (providerId: string, id: string) =>
    dispatch(lifeIdActions.getGeneralInfoRequest({ providerId, id })),
  getVitalDataById: (providerId: string, id: string) =>
    dispatch(lifeIdActions.getVitalDataRequest({ providerId, id })),
  getVitalDataIntervals: (providerId: string, id: string) =>
    dispatch(lifeIdActions.getVitalDataIntervalsRequest({ providerId, id })),
  getBehavioralDataById: (id: string) => dispatch(lifeIdActions.getBehavioralDataRequest(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LifeId);
